import type { SwitchTradeCommandData } from '@festina/unitlink-general-types/unitlink'
import { notEmpty, typeUtils } from '@festina-life/core'
import { FormikMultiSelect } from '@festina-life/ui/formikForms'
import { useTranslate } from '@festina-life/ui/hooks'
import { useDataContext } from '@festina-life/ui/theme'
import { useMemo } from 'react'

import { useTime } from '../../../hooks/useTime'
import { useHook } from '../../../lenteHooks'
import { useRenderPortfolioKeys } from '../../../pages/portfolio/useRenderPortfolioKeys'

type SubPortfolioKeyInputs = Pick<
  SwitchTradeCommandData,
  'subPortfolioGroupKeys' | 'subPortfolioKeys' | 'resetSubPortfolioKeys'
>

const validateKey = typeUtils.createValidator<SubPortfolioKeyInputs>()

export const SubPortfolioKeyInputs = () => {
  const translate = useTranslate()
  const { data: time } = useTime()
  const commitmentDateProps = {
    currentOverrideCommitmentVariant: 'currentByCommitmentDate' as const,
    commitmentDate: useDataContext().expectedApplyDate ?? time,
  }
  const renderPortfolioKeys = useRenderPortfolioKeys(commitmentDateProps)
  const toSubCommitmentName = useHook('pages.commitment.useSubCommitmentName')(
    commitmentDateProps,
  )
  const { tenantId, ownerId } = useHook('routing.general.useEntityParams')()
  const { data: portfolio } = useHook(
    'fetchers.unitlink.useFetchPortfolioVersionLatest',
  )({
    tenantId,
    ownerId,
  })
  const { data } = useHook('fetchers.unitlink.useFetchPortfolioVersionLatest')({
    ownerId,
    tenantId,
  })

  const subPortfolioKeysOptions = useMemo(
    () =>
      data?.subPortfolioGroupVersions
        ?.flatMap((x) =>
          x?.subPortfolioVersions?.map((y) =>
            y?.subPortfolioKey && y.subPortfolioRef
              ? y.subPortfolioKey
              : undefined,
          ),
        )
        .filter(notEmpty) ?? [],
    [data?.subPortfolioGroupVersions],
  )

  const subPortfolioGroupKeysOptions = useMemo(
    () =>
      portfolio?.subPortfolioGroupVersions
        ?.map((group) => group?.subPortfolioGroupKey)
        ?.filter(notEmpty) ?? [],
    [portfolio?.subPortfolioGroupVersions],
  )

  return (
    <>
      <FormikMultiSelect
        name={validateKey('subPortfolioGroupKeys')}
        options={subPortfolioGroupKeysOptions}
        label={translate('subPortfolioKeyInputs.subPortfolioGroupKey')}
        renderFn={(subPortfolioGroupKey) =>
          toSubCommitmentName(subPortfolioGroupKey)
        }
      />
      <FormikMultiSelect
        name={validateKey('resetSubPortfolioKeys')}
        label={translate('subPortfolioKeyInputs.resetSubPortfolioKeys')}
        options={subPortfolioKeysOptions}
        renderFn={(subPortfolioKey) =>
          renderPortfolioKeys({
            subPortfolioGroupKey: undefined,
            subPortfolioKey,
          })
        }
      />
      <FormikMultiSelect
        name={validateKey('subPortfolioKeys')}
        options={subPortfolioKeysOptions}
        label={translate('subPortfolioKeyInputs.subPortfolioKey')}
        renderFn={(subPortfolioKey) =>
          renderPortfolioKeys({
            subPortfolioGroupKey: undefined,
            subPortfolioKey,
          })
        }
      />
    </>
  )
}
